<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { live, stats, ui } from '../editor/bridge';
import { formatBytes, t } from '../i18n';
import { metrics, settings } from '../platform';
import { runtimeStats } from '../modules';
import Icon from './Icon.vue';

/** Local diagnostics panel (§14.4). Unsupported metrics are shown as unavailable, never invented. */
const tick = ref(0);
let timer: ReturnType<typeof setInterval> | null = null;
const count = ref(5000);
const root = ref<HTMLElement | null>(null);
const dragging = ref(false);

function startDrag(e: PointerEvent): void {
  if ((e.target as HTMLElement).closest('button')) return;
  const el = root.value;
  const parent = el?.offsetParent as HTMLElement | null;
  if (!el || !parent) return;
  const box = el.getBoundingClientRect();
  const host = parent.getBoundingClientRect();
  const dx = e.clientX - box.left;
  const dy = e.clientY - box.top;
  dragging.value = true;
  e.preventDefault();
  const move = (ev: PointerEvent) => {
    const w = el.offsetWidth;
    settings.perfPanelPos = {
      left: Math.min(Math.max(ev.clientX - dx - host.left, 8 - w + 80), host.width - 80),
      top: Math.min(Math.max(ev.clientY - dy - host.top, 8), host.height - 40),
    };
  };
  const up = () => {
    dragging.value = false;
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
  };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
}

const place = computed(() => {
  const p = settings.perfPanelPos;
  if (!p) return undefined;
  return { left: p.left + 'px', top: p.top + 'px', bottom: 'auto' };
});

onMounted(() => {
  timer = setInterval(() => tick.value++, 500);
  live.renderer?.invalidate();
});
onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
});

const mem = computed(() => {
  void tick.value;
  const m = (performance as Performance & { memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number } }).memory;
  return m ? `${formatBytes(m.usedJSHeapSize)} / ${formatBytes(m.jsHeapSizeLimit)}` : t('perf.unavailable');
});

const rows = computed(() => {
  void tick.value;
  void ui.docRev;
  const s = stats.value;
  const ed = live.editor;
  const idx = ed?.scene.index.stats();
  const fmt = (name: string) => {
    const m = metrics.get(name);
    return m ? `${m.last.toFixed(1)} (${m.avg.toFixed(1)})` : '—';
  };
  const rt = runtimeStats();
  return [
    [t('perf.fps'), s ? s.fps.toFixed(0) : '—'],
    [t('perf.frame'), s ? `${s.frameMs.toFixed(1)} (${s.frameMsAvg.toFixed(1)})` : '—'],
    [t('perf.backend'), s?.backend ?? ui.backend],
    [t('perf.objects'), String(ed?.doc.size ?? 0)],
    [t('perf.visible'), String(s?.visible ?? 0)],
    [t('perf.drawCalls'), String(s?.drawCalls ?? 0)],
    [t('perf.textures'), s ? `${s.textures} · ${formatBytes(s.textureBytes)}` : '—'],
    [t('perf.memory'), mem.value],
    [t('perf.index'), idx ? `${idx.entries} / h${idx.height}` : '—'],
    [t('perf.workers'), rt],
    [t('perf.api'), fmt('module.api')],
    [t('perf.autosave'), fmt('autosave')],
    [t('perf.load'), fmt('canvas.load')],
  ];
});

function generate(): void {
  live.editor?.generate(count.value);
  live.editor?.fitAll();
}

function toggleBackend(): void {
  settings.renderer = settings.renderer === 'webgl2' ? 'canvas2d' : 'webgl2';
}
</script>

<template>
  <section ref="root" class="float card perf" :class="{ dragging }" :style="place" :aria-label="t('perf.title')">
    <header @pointerdown="startDrag">
      <strong>{{ t('perf.title') }}</strong>
      <button class="icon-btn" :aria-label="t('common.close')" @click="settings.perfPanel = false"><Icon name="close" :size="14" /></button>
    </header>
    <table>
      <tr v-for="[k, v] in rows" :key="k">
        <th>{{ k }}</th>
        <td>{{ v }}</td>
      </tr>
    </table>
    <div class="actions">
      <select v-model.number="count" class="num-pill wide" :aria-label="t('perf.generate')">
        <option :value="1000">1 000</option>
        <option :value="5000">5 000</option>
        <option :value="25000">25 000</option>
        <option :value="100000">100 000</option>
      </select>
      <button class="pill" :disabled="ui.readOnly" @click="generate">{{ t('perf.generate') }}</button>
      <button class="pill" @click="toggleBackend">{{ t('perf.switchBackend') }}</button>
    </div>
  </section>
</template>

<style scoped>
.perf {
  left: 64px;
  bottom: 12px;
  width: 290px;
  font-size: 12px;
  padding: 6px 12px 10px;
}
.perf.dragging {
  z-index: 40;
}
header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: grab;
  touch-action: none;
}
.perf.dragging,
.perf.dragging header {
  cursor: grabbing;
}
header .icon-btn {
  width: 24px;
  height: 24px;
  cursor: pointer;
}
table {
  width: 100%;
  border-collapse: collapse;
  font-variant-numeric: tabular-nums;
}
th {
  text-align: left;
  font-weight: 400;
  color: var(--text-3);
  padding: 1px 0;
}
td {
  text-align: right;
}
.actions {
  display: flex;
  gap: 6px;
  margin-top: 8px;
}
.actions .input {
  height: 26px;
}
</style>
